import React, { useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';
import { useLocation, useNavigate } from 'react-router-dom';
import { apiService } from '../../services/api';
import { formatDateTimeUTC } from '../../utils/dateTime';
import { CliOperation, CliUsageAggregates, CliUsageDay } from '../../types';
import ConvertedTokensChart from './ConvertedTokensChart';
import UsageBreakdownChart from './UsageBreakdownChart';
import ApiKeyFilter, { CLI_SCOPE } from '../filters/ApiKeyFilter';
import DateRangeFilter from '../filters/DateRangeFilter';
import { idsParam } from '../filters/EntityFilter';
import { EmptyRow, SectionFilterBar, SortableTh, TableCard, TableHeader, TableViewport, tdClass, usePageSize } from '../table/TableKit';
import { StatTile } from '../cards/StatCards';
import { Badge } from '../StatusBadge';
import { CliCost, tokensForOperation } from './cliCost';
import { ErrorNotice } from '../ErrorNotice';
import RefreshButton from '../RefreshButton';
import { convertedTokensByDay, KindUsageRow, TokenKind } from './usageBreakdown';
import { usePageClamp } from '../../hooks/usePageClamp';
import { useUsageRange } from '../../hooks/useUsageRange';
import { urlEnum, urlIdList, urlIsoDate, urlPositiveInt, useUrlState } from '../../hooks/useUrlState';
import { fromHere } from '../../hooks/useBackTo';
import { apiKeyName, maskApiKey } from '../../utils/apiKey';

// Fixed height so every control in the filter bar lines up.
const filterClass =
    'h-10 bg-body-bg border border-gray-600 text-body-color focus:border-primary focus:outline-none rounded-md pl-3 pr-8 py-2 select-chevron';

const emptyAggregates: CliUsageAggregates = {
    unique_count: 0,
    duplicate_count: 0,
    input_tokens_sum: 0,
    input_tokens_converted_sum: 0,
    input_tokens_supported_sum: 0,
    output_tokens_sum: 0,
};

// Users never see output tokens.
const CHART_TOKEN_KINDS: TokenKind[] = ['converted', 'supported', 'input'];

const URL_SPEC = {
    keys: urlIdList(),
    operation: urlEnum(['', 'analyze', 'convert'] as const, ''),
    status: urlEnum(['', 'authorized', 'success', 'failed', 'expired'] as const, ''),
    range: urlEnum(['7d', '30d', 'custom'] as const, '30d'),
    from: urlIsoDate(),
    to: urlIsoDate(),
    sort: urlEnum(['project_name', 'operation', 'api_key', 'status', 'file_count', 'input_tokens', 'input_tokens_converted', 'input_tokens_supported', 'cost', 'version', 'authorized_at'] as const, 'authorized_at'),
    order: urlEnum(['asc', 'desc'] as const, 'desc'),
    page: urlPositiveInt(),
};

function CliUsageTab({ urlPrefix }: { urlPrefix?: string }) {
    const navigate = useNavigate();
    const location = useLocation();
    const [state, setUrlState] = useUrlState(URL_SPEC, urlPrefix);
    const [limit, setLimit] = usePageSize('cli-ops');
    const offset = (state.page - 1) * limit;
    const { rangeFilter, range } = useUsageRange(state.range, state.from, state.to);
    const apiKeyIdsParam = idsParam(state.keys);

    const params = useMemo(
        () => ({
            api_key_ids: apiKeyIdsParam,
            operation: state.operation || undefined,
            status: state.status || undefined,
            from: range?.from,
            to: range?.to,
            limit,
            offset,
            sort: state.sort,
            order: state.order,
        }),
        [apiKeyIdsParam, state.operation, state.status, range, limit, offset, state.sort, state.order],
    );

    const { data: fetched, isFetching, isLoading, isPlaceholderData, error, refetch } = useQuery({
        queryKey: ['converter', 'cliUsage', params],
        queryFn: async () => (await apiService.cliGetUsage(params)).data,
        placeholderData: (prev) => prev,
        retry: false,
        enabled: range !== null,
        refetchOnWindowFocus: true,
    });
    // An incomplete custom range fetches nothing. Drop the old numbers: kept on
    // screen they would read as this range's.
    const data = range ? fetched : undefined;

    const dailyParams = useMemo(
        () => ({
            api_key_ids: apiKeyIdsParam,
            operation: state.operation || undefined,
            status: state.status || undefined,
            from: range?.from,
            to: range?.to,
        }),
        [apiKeyIdsParam, state.operation, state.status, range],
    );

    const { data: daily, error: dailyError, isFetching: isDailyFetching, refetch: refetchDaily } = useQuery({
        queryKey: ['converter', 'cliUsageDaily', dailyParams],
        queryFn: async () => (await apiService.cliGetUsageDaily(dailyParams)).data,
        retry: false,
        enabled: range !== null,
        refetchOnWindowFocus: true,
    });

    const convertedRows = useMemo(() => range ? convertedTokensByDay(daily?.days ?? [], range) : [], [daily?.days, range]);

    const chartRows: KindUsageRow[] = useMemo(
        () =>
            (daily?.days ?? []).map((day: CliUsageDay) => ({
                date: day.date,
                keyId: day.api_key_id,
                keyLabel: day.name || maskApiKey(day),
                tokensByKind: { input: day.input_tokens, converted: day.input_tokens_converted, supported: day.input_tokens_supported, output: 0 },
                count: day.operations,
            })),
        [daily],
    );

    const operations: CliOperation[] = data?.operations ?? [];
    const aggregates = data?.aggregates ?? emptyAggregates;
    const total = data?.total ?? 0;
    // A background refetch keeps the current rows at full opacity.
    const rowsPending = isLoading || (isFetching && isPlaceholderData);

    usePageClamp({
        page: state.page,
        pageSize: limit,
        total: data?.total,
        ready: !isLoading && !isPlaceholderData && !!data,
        setPage: (page) => setUrlState({ page }),
    });

    const onSort = (sort: string, order: 'asc' | 'desc') =>
        setUrlState({ sort: sort as typeof state.sort, order, page: 1 });

    const tiles = [
        { label: 'Operations', value: total },
        { label: 'Unique Entries', value: aggregates.unique_count },
        { label: 'Duplicate Entries', value: aggregates.duplicate_count },
        { label: 'In Tokens', value: aggregates.input_tokens_sum },
        { label: 'In Tokens (Converted)', value: aggregates.input_tokens_converted_sum },
        { label: 'In Tokens (Supported)', value: aggregates.input_tokens_supported_sum },
    ];

    return (
        <div>
            {error && <ErrorNotice message="Failed to load CLI usage." />}
            {/* Tab-wide filters: they scope the chart, the tiles, and the tables. */}
            <SectionFilterBar>
                <ApiKeyFilter scope={CLI_SCOPE} value={state.keys} onChange={(keys) => setUrlState({ keys, page: 1 })} className="h-10 text-base" />
                <select
                    aria-label="Operation filter"
                    className={filterClass}
                    value={state.operation}
                    onChange={(e) => setUrlState({ operation: e.target.value as typeof state.operation, page: 1 })}
                >
                    <option value="">All Operations</option>
                    <option value="analyze">analyze</option>
                    <option value="convert">convert</option>
                </select>
                <select
                    aria-label="Status filter"
                    className={filterClass}
                    value={state.status}
                    onChange={(e) => setUrlState({ status: e.target.value as typeof state.status, page: 1 })}
                >
                    <option value="">All Statuses</option>
                    <option value="authorized">authorized</option>
                    <option value="success">success</option>
                    <option value="failed">failed</option>
                    <option value="expired">expired</option>
                </select>
                <DateRangeFilter
                    value={rangeFilter}
                    onChange={(next) => setUrlState({ range: next.preset, from: next.from, to: next.to, page: 1 })}
                    className="!h-10"
                />
                <RefreshButton
                    onClick={() => {
                        refetch();
                        refetchDaily();
                    }}
                    fetching={isFetching || isDailyFetching}
                    disabled={!range}
                />
            </SectionFilterBar>
            <div role="group" aria-label="Usage totals" className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4 mb-6">
                {tiles.map((tile) => (
                    <StatTile key={tile.label} label={tile.label} value={tile.value.toLocaleString()} />
                ))}
            </div>
            <UsageBreakdownChart rows={chartRows} range={range} countLabel="Operations" tokenKinds={CHART_TOKEN_KINDS} error={!!dailyError} urlPrefix={urlPrefix} />
            <ConvertedTokensChart rows={convertedRows} range={range} error={!!dailyError} />

            <TableCard>
                <TableHeader
                    title="Operations"
                    pagination={{
                        offset,
                        limit,
                        total,
                        onOffsetChange: (nextOffset) => setUrlState({ page: nextOffset / limit + 1 }),
                        onLimitChange: (size) => {
                            setLimit(size);
                            setUrlState({ page: 1 });
                        },
                    }}
                />
                <TableViewport className={rowsPending ? "opacity-50 transition-opacity" : ""}>
                    <table className="w-full">
                        <thead>
                            <tr className="border-b border-gray-700">
                                <SortableTh label="Project" sortKey="project_name" sort={state.sort} order={state.order} onSort={onSort} firstOrder="asc" />
                                <SortableTh label="Operation" sortKey="operation" sort={state.sort} order={state.order} onSort={onSort} firstOrder="asc" />
                                <SortableTh label="API Key" sortKey="api_key" sort={state.sort} order={state.order} onSort={onSort} firstOrder="asc" />
                                <SortableTh label="Status" sortKey="status" sort={state.sort} order={state.order} onSort={onSort} firstOrder="asc" />
                                <SortableTh label="Files" sortKey="file_count" sort={state.sort} order={state.order} onSort={onSort} />
                                <SortableTh label="In Tokens" sortKey="input_tokens" sort={state.sort} order={state.order} onSort={onSort} />
                                <SortableTh label="In Tokens (Converted)" sortKey="input_tokens_converted" sort={state.sort} order={state.order} onSort={onSort} />
                                <SortableTh label="In Tokens (Supported)" sortKey="input_tokens_supported" sort={state.sort} order={state.order} onSort={onSort} />
                                <SortableTh label="Cost" sortKey="cost" sort={state.sort} order={state.order} onSort={onSort} />
                                <SortableTh label="Version" sortKey="version" sort={state.sort} order={state.order} onSort={onSort} firstOrder="asc" />
                                <SortableTh label="Authorized At" sortKey="authorized_at" sort={state.sort} order={state.order} onSort={onSort} />
                            </tr>
                        </thead>
                        <tbody className="divide-y divide-gray-700">
                            {operations.map((op) => (
                                <tr
                                    key={op.id}
                                    className="cursor-pointer hover:bg-body-bg/50"
                                    onClick={() => navigate(`/usage/cli/operations/${op.id}`, { state: fromHere(location) })}
                                >
                                    <td className={tdClass}>{op.project_name}</td>
                                    <td className={tdClass}>{op.operation}</td>
                                    <td className={tdClass}>{apiKeyName(op)}</td>
                                    <td className="px-4 py-3 text-sm"><Badge value={op.status} /></td>
                                    <td className={tdClass}>{op.file_count ?? '—'}</td>
                                    <td className={tdClass}>{op.input_tokens_sum.toLocaleString()}</td>
                                    <td className={tdClass}>{tokensForOperation(op.operation, 'converted', op.input_tokens_converted_sum)}</td>
                                    <td className={tdClass}>{tokensForOperation(op.operation, 'supported', op.input_tokens_supported_sum)}</td>
                                    <td className={tdClass}><CliCost operation={op.operation} cost={op.cost_cents} estimate={op.estimated_cost_cents} /></td>
                                    <td className={tdClass}>{op.alchemist_version}</td>
                                    <td className={tdClass}>{formatDateTimeUTC(op.authorized_at)}</td>
                                </tr>
                            ))}
                            {operations.length === 0 && (
                                <EmptyRow
                                    colSpan={11}
                                    text={rowsPending ? 'Loading...' : range ? 'No CLI operations found.' : 'Pick a valid date range.'}
                                />
                            )}
                        </tbody>
                    </table>
                </TableViewport>
            </TableCard>
        </div>
    );
}

export default CliUsageTab;
